import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaSpinner, FaEnvelope, FaLock } from 'react-icons/fa';
import { FaUser } from 'react-icons/fa';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || '/dashboard';

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setError('');
            setLoading(true);
            const result = await login(email, password);

            // Check if system is busy (load balance)
            if (result && result.status === 'busy') {
                navigate('/waiting-room', {
                    state: {
                        message: result.message,
                        waitEstimate: result.data?.wait_time_estimate
                    }
                });
                return;
            }

            navigate(from, { replace: true });
        } catch (err) {
            setError('Failed to log in: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-blue-50">
            {/* Background Decorations (Cloud/Sky effect) */}
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-200 via-blue-100 to-white"></div>

            {/* Animated Blobs */}
            <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>

            {/* Animated Background Lines */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[10%] left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-400 to-transparent animate-line opacity-20"></div>
                <div className="absolute top-[30%] left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-400 to-transparent animate-line animation-delay-2000 opacity-20"></div>
                <div className="absolute top-[50%] left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-line animation-delay-4000 opacity-20"></div>
                <div className="absolute top-[70%] left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500 to-transparent animate-line animation-delay-2000 opacity-20"></div>
                <div className="absolute top-[90%] left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-400 to-transparent animate-line animation-delay-4000 opacity-20"></div>
            </div>

            {/* Login Card */}
            <div className="relative w-full max-w-md p-8 bg-white/60 backdrop-blur-2xl rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/50 m-4">

                {/* Logo/Icon */}
                <div className="flex justify-center mb-6">
                    <div className="w-14 h-14 bg-white rounded-2xl shadow-lg flex items-center justify-center text-gray-800">
                        <FaUser size={26} />
                    </div>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Sign in with email</h2>
                    <p className="mt-2 text-sm text-gray-500 font-medium">
                        Make a new doc to bring your words, data, and teams together. For free
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50/80 border border-red-100 rounded-2xl text-red-600 text-sm font-medium text-center">
                        {error}
                    </div>
                )}

                {/* Form */}
                <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaEnvelope className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Email"
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Password"
                            />
                        </div>
                        <div className="flex justify-end pt-1">
                            <Link to="/forgot-password" title="Forgot Password" className="text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors">
                                Forgot password?
                            </Link>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 flex items-center justify-center bg-gray-900 hover:bg-gray-800 text-white rounded-2xl font-bold transition-all duration-200 transform active:scale-95 shadow-lg shadow-gray-900/20"
                    >
                        {loading ? <FaSpinner className="animate-spin h-5 w-5" /> : 'Get Started'}
                    </button>
                </form>



                {/* Sign Up Link */}
                <p className="mt-8 text-center text-sm text-gray-500 font-medium">
                    Don't have an account?{' '}
                    <Link to="/register" className="text-gray-900 font-bold hover:underline">
                        Sign up
                    </Link>
                </p>

            </div>
        </div>
    );
}
